import { useEffect, useState } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  SelectChangeEvent,
  SelectProps,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllowedDepartements } from "../../reduxStore/ScopeSlice";
import { RootState, AppDispatch } from "../../reduxStore/Store";
import { Departement } from "../../models/models";

interface SelectDepartementProps
  extends Omit<SelectProps<number | string>, "value" | "onChange"> {
  onDepartementChange: (departement: Departement | null) => void;
  selectedDepartement?: Departement | null;
  allowAll?: boolean; // affiche une option "vide" (tous départements / partagé) en première position
  allLabel?: string;  // libellé de cette option — défaut "Tous les départements"
}

const SelectDepartement: React.FC<SelectDepartementProps> = ({
  onDepartementChange,
  selectedDepartement,
  allowAll = false,
  allLabel = "Tous les départements",
  ...selectProps
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { allowedDepartements: departements, loading, error } = useSelector(
    (state: RootState) => state.ScopeSlice
  );
  const [deptId, setDeptId] = useState<number | string>("");

  useEffect(() => {
    dispatch(fetchAllowedDepartements());
  }, [dispatch]);

  useEffect(() => {
    setDeptId(selectedDepartement?.id ?? "");
  }, [selectedDepartement]);

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    const selectedId = event.target.value;
    if (selectedId === "" || selectedId === 0) {
      setDeptId("");
      onDepartementChange(null);
      return;
    }
    const found = departements.find((d) => d.id === Number(selectedId)) || null;
    setDeptId(selectedId);
    onDepartementChange(found);
  };

  return (
    <FormControl fullWidth size="small" variant="outlined" error={!!error}>
      <InputLabel id="select-departement-label">Département</InputLabel>
      <Select
        labelId="select-departement-label"
        value={deptId}
        onChange={handleChange}
        label="Département"
        disabled={loading}
        size="small"
        {...selectProps}
      >
        {allowAll && <MenuItem value=""><em>{allLabel}</em></MenuItem>}
        {departements.length === 0 ? (
          <MenuItem value="">Aucun département trouvé</MenuItem>
        ) : (
          departements.map((dept) => (
            <MenuItem key={dept.id} value={dept.id}>
              {dept.nom}
            </MenuItem>
          ))
        )}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default SelectDepartement;
